import React, { useEffect, useMemo, useRef, useState } from "react";
import propTypes from "prop-types";
import { Button, TextField } from "@mui/material";
import { FiClock } from "react-icons/fi";

import FloatingInputLabel from "../components/FloatingInputLabel";

import flex from "../../../../styles/flex";

import { useDate, isValidDate } from "../../../../utility/useDate";
import { DateTime } from "luxon";

const TimeField: React.FunctionComponent<{
  label: string,
  value?: string | null;
  readOnly?: boolean;
  fiveMinutes?: boolean;
  onChange: (value: string) => void;
  testId?: string;
  memoise?: boolean;
  localise?: boolean;
}> = (props) => {
  const { localizeDate } = useDate();
  const fiveMinutes = props.fiveMinutes != false; //Default to 5 minute intervals

  const time = new Date(props.value == null ? "" : props.value);
  const localized = props.localise === false
    ? DateTime.fromJSDate(time, { zone: "UTC" })
    : localizeDate(time);

  const initHour: number = localized.isValid ? localized.hour : -1;
  let initMinute: number = localized.isValid ? localized.minute : -1;

  if (fiveMinutes && initMinute !== -1) {
    initMinute = (Math.round(initMinute / 5) * 5);
    if (initMinute > 55) {
      initMinute = 55;
    }
  }

  const [showPicker, setShowPicker] = useState(false);

  const hourRef = useRef<HTMLInputElement>(null);
  const minuteRef = useRef<HTMLInputElement>(null);

  const minuteSelectedRef = useRef<HTMLDivElement>(null);
  const hourSelectedRef = useRef<HTMLDivElement>(null);

  const [hour, setHour] = useState(initHour);
  const [minute, setMinute] = useState(initMinute);

  const onTimeChange = (h: number, m: number) => {
    if (isNaN(h) || h == -1) {
      h = 0;
    }

    if (isNaN(m) || m == -1) {
      m = 0;
    }

    const now = new Date();
    const defaultDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    const currentValue = new Date(props.value == null ? "" : props.value);
    const localizedDate = props.localise === false
      ? DateTime.fromJSDate(isValidDate(currentValue) ? currentValue : defaultDate)
      : localizeDate(isValidDate(currentValue) ? currentValue : defaultDate);
    const localizedDateWithTimeInput = localizedDate.set({ hour: h, minute: m });

    const isoString = localizedDateWithTimeInput.toISO();
    if (isoString) {
      props.onChange(isoString);
    }
  }

  const getAvailableHours = () => {
    const hours = [];
    for (let i = 0; i < 24; i++) {
      hours.push(i.toString().padStart(2, "0"));
    }
    return hours;
  }

  const getAvailableMinutes = (fiveMinutes: boolean) => {
    let interval = 0;
    let increment = 5;
    let cycles = 12;
    if (fiveMinutes == false) {
      increment = 1;
      cycles = 60;
    }
    const minutes = [];
    for (let i = 0; i < cycles; i++) {
      minutes.push(interval.toString().padStart(2, "0"));
      interval += increment;
    }
    return minutes;
  }

  const getHourButtons = (hours: string[], current: number) => {
    return hours.map((h, key) => {
      const selected = Number(h) === current;
      return <div ref={selected == true ? hourSelectedRef : null} style={{ cursor: "pointer", textAlign: "center", width: 64 }} key={key}>

        <Button
          variant={selected ? "contained" : "text"}
          color={selected ? "secondary" : "inherit"}
          onMouseDown={() => {
            setHour(parseInt(h));
            onTimeChange(parseInt(h), minute);
            setTimeout(() => {
              if (minuteRef.current) {
                minuteRef.current.focus();
              }
              _showPicker();
            }, 100);
          }}>
          {h}
        </Button>
      </div>
    })
  }

  const getMinuteButtons = (minutes: string[], current: number) => {
    return minutes.map((m, key) => {
      const selected = Number(m) === current;
      return <div ref={selected == true ? minuteSelectedRef : null} style={{ cursor: "pointer", textAlign: "center", width: 64 }} key={key}>
        <Button
          variant={selected ? "contained" : "text"}
          color={selected ? "secondary" : "inherit"}
          onMouseDown={() => {
            setMinute(parseInt(m));
            _hidePicker();
            onTimeChange(hour, parseInt(m));
          }} style={{ margin: 0 }}>{m}
        </Button>
      </div>
    });
  }

  const hours = props.memoise
    ? useMemo(() => getHourButtons(getAvailableHours(), hour), [hour, minute])
    : getHourButtons(getAvailableHours(), hour);
  const minuteList = props.memoise
    ? useMemo(() => getAvailableMinutes(fiveMinutes), [fiveMinutes])
    : getAvailableMinutes(fiveMinutes);
  const minutes = props.memoise
    ? useMemo(() => getMinuteButtons(minuteList, minute), [hour, minute])
    : getMinuteButtons(minuteList, minute);

  const [cancel, setCancel] = useState<NodeJS.Timeout | null>(null);

  const _showPicker = () => {
    if (typeof window === 'undefined') {
      return;
    }
    if (cancel != null) {
      clearTimeout(cancel);
    }
    setShowPicker(true);
  }
  const _hidePicker = () => {
    if (typeof window === 'undefined') {
      return;
    }
    setCancel(setTimeout(() => {
      setShowPicker(false);
    }, 200));
  }

  useEffect(() => {
    if (minuteSelectedRef.current) {
      if (minuteSelectedRef.current.parentElement) {
        minuteSelectedRef.current.parentElement.scrollTop = minuteSelectedRef.current.offsetTop;
      }
    }
  }, [minuteSelectedRef, showPicker, minute]);

  useEffect(() => {
    if (hourSelectedRef.current) {
      if (hourSelectedRef.current.parentElement) {
        hourSelectedRef.current.parentElement.scrollTop = hourSelectedRef.current.offsetTop;
      }
    }
  }, [hourSelectedRef, showPicker, hour]);

  useEffect(() => {
    if (props.value != null) {
      const propTime = new Date(props.value);
      if (isValidDate(propTime)) {
        const localized = localizeDate(propTime);
        if ((localized.hour != initHour && initHour > -1) || (localized.minute != initMinute && initMinute > -1)) {
          onTimeChange(initHour, initMinute);
        }
      }
    }
  }, [props.value]);

  useEffect(() => {
    setHour(initHour);
    setMinute(initMinute);
  }, [initHour, initMinute])

  return (
    <FloatingInputLabel label={props.label} >
      <div style={{ border: "solid 1px rgb(196, 196, 196)", borderRadius: 5, padding: 5, display: "inline-block", position: "relative" }}>
        <div style={flex.flexRows}>
          <div style={flex.flexAuto}>
            <div style={{ paddingLeft: 5 }}>
              <TextField
                inputRef={hourRef}
                variant="standard"
                type="number"
                size="small"
                placeholder={"HH"}
                disabled={props.readOnly == true}
                value={(hour == -1 || isNaN(hour)) ? "" : hour.toString().padStart(2, "0")}
                style={{ textTransform: "none", width: 38 }}
                inputProps={
                  {
                    style: {
                      textAlign: 'center',
                    },
                    'data-testid': `HH_input_${props.testId}`,
                  }
                }
                onFocus={() => {
                  if (hourRef.current) {
                    hourRef.current.select();
                  }
                  _showPicker();
                }}
                onBlur={() => {
                  _hidePicker();
                  onTimeChange(hour, minute);
                }}
                onChange={() => {
                  if (hourRef.current) {
                    let hourSet = parseInt(hourRef.current.value);
                    if (hourSet < 0) {
                      hourSet = 0;
                    } else if (hourSet > 23) {
                      hourSet = 23;
                    }
                    setHour(hourSet);
                    if (hourSet.toString().length == 2) {
                      if (typeof window !== 'undefined') {
                        setTimeout(() => {
                          if (minuteRef.current) {
                            minuteRef.current.focus();
                          }
                          _showPicker();
                        }, 100);
                      }
                    } else if (hourSet.toString().length == 1) {
                      if (hourSet > 2) {
                        if (typeof window !== 'undefined') {
                          setTimeout(() => {
                            if (minuteRef.current) {
                              minuteRef.current.focus();
                            }
                            _showPicker();
                          }, 100);
                        }
                      }
                    }

                  }
                }}
              />


            </div>

          </div>
          <div style={flex.flexAuto}>
            <div style={{ paddingTop: 2 }}>
              :
            </div>
          </div>
          <div style={flex.flexAuto}>
            <div style={{ position: "relative" }}>
              <TextField
                inputRef={minuteRef}
                variant="standard"
                type="number"
                size="small"
                placeholder={"MM"}
                disabled={props.readOnly == true}
                value={(minute == -1 || isNaN(minute)) ? "" : minute.toString().padStart(2, "0")}
                style={{ textTransform: "none", width: 38 }}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={
                  {
                    style: {
                      textAlign: 'center'
                    },
                    'data-testid': `MM_input_${props.testId}`,
                  }
                }
                onFocus={() => {
                  if (minuteRef.current) {
                    minuteRef.current.select();
                  }
                  _showPicker();
                }}
                onBlur={() => {
                  _hidePicker();
                  onTimeChange(hour, minute);
                }}
                onChange={() => {
                  if (minuteRef.current) {
                    let minuteSet = parseInt(minuteRef.current.value);
                    if (minuteSet < 0) {
                      minuteSet = 0;
                    } else if (minuteSet > 55) {
                      if (fiveMinutes == true) {
                        minuteSet = 55;
                      } else {
                        minuteSet = 59;
                      }
                    }
                    if (minuteSet.toString().length == 2) {
                      const m = minuteSet.toString()[0];
                      const mins = parseInt(minuteSet.toString()[1]);

                      const mm = fiveMinutes ? ((Math.round(mins) / 5) * 5) : mins;

                      minuteSet = parseInt(`${m}${mm}`);

                    }
                    setMinute(minuteSet);

                  }
                }}
              />

            </div>
          </div>
          <div style={flex.flexAuto}>

            <div onClick={() => {
              if (hourRef.current) {
                hourRef.current.focus();
              }
            }} style={{ padding: 4, width: 26, textAlign: "left" }}>
              <div style={{ paddingLeft: 4 }}>
                <FiClock size={18}></FiClock>
              </div>
            </div>

          </div>
        </div>

        <div style={{
          zIndex: 9999,
          position: "absolute",
          backgroundColor: "white",
          display: "inline-block",
          left: 0,
          right: 0,
          marginTop: 5.5,
          visibility: showPicker == true ? "visible" : "collapse",
          border: "solid 1px gray",
        }}>
          <div style={flex.flexRows}>
            <div style={flex.flexFill}>
              <div className={"hidescroll"} style={{
                maxHeight: 170,
                overflowY: "scroll",
              }}>
                {hours}
              </div>
            </div>
            <div style={flex.flexFill}>
              <div className={"hidescroll"} style={{
                maxHeight: 170,
                overflowY: "scroll",
              }}>
                {minutes}
              </div>

            </div>
          </div>
        </div>

      </div>

    </FloatingInputLabel>
  );
}
TimeField.propTypes = {
  label: propTypes.string.isRequired,
  value: propTypes.any,
  onChange: propTypes.func.isRequired,
  readOnly: propTypes.bool,
  fiveMinutes: propTypes.bool,

}

export default TimeField;
